import { ReactNode } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { WorkflowDetail } from "../../api/workflows";

// Top section on the export detail page: the inputs the workflow was
// submitted with, preserved verbatim. Lets the user re-run an
// identical export or hand the config to a colleague without
// spelunking through database rows.
//
// Renders known fields in a sensible order with type-aware formatting
// (string / number / list / inline code block for monospaced
// queries). Anything else in ``params_json`` lands in the
// "Additional parameters" tail so unknown / future fields aren't
// silently hidden.

interface Props {
  wf: WorkflowDetail;
}

// Fields we know how to label and order. Keys are matched against
// ``params_json`` plus a few synthetic top-level workflow attributes
// (``kind``, ``submitted_by``, ``submitted_at``).
interface FieldDef {
  key: string;
  label: string;
  // ``code`` → monospaced inline / wrapping code block. Used for
  // S3 URIs, filter predicates, Lucene queries — anything where the
  // exact characters matter.
  render?: "default" | "code" | "chips" | "list";
}

const FIELD_ORDER: FieldDef[] = [
  { key: "kind", label: "Workflow kind" },
  { key: "submitted_by", label: "Submitted by" },
  { key: "submitted_at", label: "Submitted at" },
  { key: "year", label: "Year" },
  { key: "month", label: "Month" },
  { key: "months", label: "Months", render: "chips" },
  { key: "bucket", label: "Source bucket" },
  { key: "output", label: "Output prefix", render: "code" },
  { key: "output_format", label: "Output format" },
  { key: "filter", label: "Filter", render: "code" },
  { key: "search", label: "Lucene search", render: "code" },
  { key: "max_duration", label: "Max superdoc duration" },
  { key: "concurrency", label: "Concurrency" },
  { key: "workers", label: "Workers per job" },
];

const KNOWN_KEYS = new Set(FIELD_ORDER.map((f) => f.key));

export function JobConfigurationPanel({ wf }: Props) {
  const params: Record<string, unknown> = (() => {
    try {
      return JSON.parse(wf.params_json || "{}");
    } catch {
      return {};
    }
  })();

  // Synthetic merged view: top-level workflow attrs first, then the
  // parsed params. ``kind`` is on the workflow row, not in params_json.
  const merged: Record<string, unknown> = {
    kind: wf.kind,
    ...(wf.submitted_by ? { submitted_by: wf.submitted_by } : {}),
    ...(wf.submitted_at
      ? { submitted_at: new Date(wf.submitted_at).toUTCString() }
      : {}),
    ...params,
  };

  const handleDownload = () => {
    const payload = {
      workflow_id: wf.id,
      kind: wf.kind,
      submitted_by: wf.submitted_by,
      submitted_at: wf.submitted_at,
      params,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `paim-config-${wf.id.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Anything in params_json we didn't put in FIELD_ORDER. Surfaces
  // new params (workflow kinds we haven't taught the panel about yet)
  // instead of dropping them.
  const unknownEntries = Object.entries(params).filter(
    ([k]) => !KNOWN_KEYS.has(k),
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Inputs the workflow was submitted with — preserved verbatim so
          you can re-run an identical export or hand it to a colleague.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          JSON
        </Button>
      </Stack>

      <Stack spacing={1}>
        {FIELD_ORDER.map((f) => {
          const v = merged[f.key];
          if (v === undefined || v === null || v === "") return null;
          return <ConfigRow key={f.key} label={f.label} value={renderValue(v, f.render)} />;
        })}
      </Stack>

      {unknownEntries.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}
          >
            Additional parameters
          </Typography>
          <Stack spacing={0.5}>
            {unknownEntries.map(([k, v]) => (
              <ConfigRow key={k} label={k} value={renderValue(v, "default")} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

function ConfigRow({ label, value }: { label: string; value: ReactNode }) {
  // Top-anchor the label so multi-line values (long Lucene queries,
  // wrapping filter expressions) don't leave the label baseline-
  // dragged to the bottom of a tall code block. The 4px pt nudge
  // brings the caption baseline back into line with the value's
  // first-line text for short single-row entries.
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="flex-start"
      sx={{ flexWrap: "wrap" }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 160, flexShrink: 0, pt: "4px" }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>{value}</Box>
    </Stack>
  );
}

function renderValue(v: unknown, mode: FieldDef["render"] = "default"): ReactNode {
  if (mode === "chips" && Array.isArray(v)) {
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
        {v.map((item, i) => (
          <Chip key={i} size="small" label={String(item)} sx={{ height: 22 }} />
        ))}
      </Stack>
    );
  }
  if (Array.isArray(v)) {
    return (
      <Typography variant="body2">{v.map((x) => String(x)).join(", ")}</Typography>
    );
  }
  if (mode === "code") {
    return (
      <Box
        component="code"
        sx={{
          display: "inline-block",
          fontFamily: "monospace",
          fontSize: 12,
          backgroundColor: "grey.50",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0.5,
          px: 0.75,
          py: 0.25,
          maxWidth: "100%",
          wordBreak: "break-all",
          whiteSpace: "pre-wrap",
        }}
      >
        {String(v)}
      </Box>
    );
  }
  if (typeof v === "object" && v !== null) {
    return (
      <Box component="code" sx={{ fontFamily: "monospace", fontSize: 12 }}>
        {JSON.stringify(v)}
      </Box>
    );
  }
  return <Typography variant="body2">{String(v)}</Typography>;
}
